(() => {
  const CLOCK_SELECTOR = '[data-countdown-clock]';
  const TIME_ZONE = 'America/Los_Angeles';
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const formatterCache = new Map();
  const activeClocks = new Set();
  let timerId = null;

  const getFormatter = (timeZone) => {
    if (!formatterCache.has(timeZone)) {
      formatterCache.set(
        timeZone,
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
      );
    }

    return formatterCache.get(timeZone);
  };

  const getZonedParts = (date, timeZone) => {
    const parts = getFormatter(timeZone).formatToParts(date);
    const lookup = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') {
        lookup[part.type] = part.value;
      }
    });

    return {
      year: Number(lookup.year),
      month: Number(lookup.month),
      day: Number(lookup.day),
      hour: Number(lookup.hour),
      minute: Number(lookup.minute),
      second: Number(lookup.second),
    };
  };

  const parseDateInput = (value) => {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
  };

  const parseTimeInput = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return { hour: 0, minute: 0 };
    }

    const match = normalized.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])?$/);
    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridiem = match[3] ? match[3].toUpperCase() : '';

    if (minute > 59) return null;

    if (meridiem) {
      if (hour < 1 || hour > 12) return null;
      if (meridiem === 'AM') {
        hour = hour === 12 ? 0 : hour;
      } else {
        hour = hour === 12 ? 12 : hour + 12;
      }
    } else if (hour > 23) {
      return null;
    }

    return { hour, minute };
  };

  const zonedDateTimeToUtcMs = ({ year, month, day, hour, minute }, timeZone) => {
    const desiredUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    let utcMs = desiredUtcMs;

    for (let i = 0; i < 3; i += 1) {
      const zonedParts = getZonedParts(new Date(utcMs), timeZone);
      const zonedUtcMs = Date.UTC(
        zonedParts.year,
        zonedParts.month - 1,
        zonedParts.day,
        zonedParts.hour,
        zonedParts.minute,
        zonedParts.second
      );
      const diff = zonedUtcMs - desiredUtcMs;

      if (Math.abs(diff) < SECOND) break;
      utcMs -= diff;
    }

    return utcMs;
  };

  const formatValue = (value, padded = true) => {
    if (!Number.isFinite(value)) return '00';
    if (!padded) return String(Math.max(0, value));
    return String(Math.max(0, value)).padStart(2, '0');
  };

  const setCompletionState = (clock, isComplete) => {
    const grid = clock.querySelector('[data-countdown-grid]');
    const completeMessage = clock.querySelector('[data-countdown-complete]');

    clock.classList.toggle('is-complete', isComplete);

    if (grid) {
      grid.hidden = isComplete;
    }

    if (completeMessage) {
      completeMessage.hidden = !isComplete;
    }
  };

  const updateClock = (clock) => {
    const targetMs = Number(clock.dataset.countdownTargetMs);
    if (!Number.isFinite(targetMs)) return false;

    const remaining = targetMs - Date.now();
    if (remaining <= 0) {
      setCompletionState(clock, true);
      return false;
    }

    setCompletionState(clock, false);

    const days = Math.floor(remaining / DAY);
    const hours = Math.floor((remaining % DAY) / HOUR);
    const minutes = Math.floor((remaining % HOUR) / MINUTE);
    const seconds = Math.floor((remaining % MINUTE) / SECOND);

    const daysNode = clock.querySelector('[data-countdown-days]');
    const hoursNode = clock.querySelector('[data-countdown-hours]');
    const minutesNode = clock.querySelector('[data-countdown-minutes]');
    const secondsNode = clock.querySelector('[data-countdown-seconds]');

    if (daysNode) daysNode.textContent = formatValue(days);
    if (hoursNode) hoursNode.textContent = formatValue(hours);
    if (minutesNode) minutesNode.textContent = formatValue(minutes);
    if (secondsNode) secondsNode.textContent = formatValue(seconds);

    return true;
  };

  const stopTimerIfIdle = () => {
    if (activeClocks.size || !timerId) return;
    window.clearInterval(timerId);
    timerId = null;
  };

  const tick = () => {
    activeClocks.forEach((clock) => {
      if (!clock.isConnected) {
        activeClocks.delete(clock);
        return;
      }

      if (!updateClock(clock)) {
        activeClocks.delete(clock);
      }
    });

    stopTimerIfIdle();
  };

  const ensureTimer = () => {
    if (timerId || !activeClocks.size) return;
    timerId = window.setInterval(tick, SECOND);
  };

  const initClock = (clock) => {
    if (clock.dataset.countdownInitialized === 'true') return;

    const dateParts = parseDateInput(clock.dataset.countdownTargetDate);
    const timeParts = parseTimeInput(clock.dataset.countdownTargetTime);
    if (!dateParts || !timeParts) return;

    const targetMs = zonedDateTimeToUtcMs(
      {
        year: dateParts.year,
        month: dateParts.month,
        day: dateParts.day,
        hour: timeParts.hour,
        minute: timeParts.minute,
      },
      clock.dataset.countdownTimeZone || TIME_ZONE
    );

    clock.dataset.countdownTargetMs = String(targetMs);
    clock.dataset.countdownInitialized = 'true';

    if (updateClock(clock)) {
      activeClocks.add(clock);
      ensureTimer();
    }
  };

  const initCountdownClocks = (scope = document) => {
    scope.querySelectorAll(CLOCK_SELECTOR).forEach(initClock);
  };

  if (window.themeCountdownClockInit) return;
  window.themeCountdownClockInit = initCountdownClocks;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCountdownClocks(document), { once: true });
  } else {
    initCountdownClocks(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCountdownClocks(event.target);
  });
})();
